import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api/client';

const TAG_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'exam', label: 'Экзамен' },
  { value: 'grade', label: 'Класс' },
  { value: 'topic', label: 'Тема' },
  { value: 'difficulty', label: 'Сложность' },
  { value: 'year', label: 'Год' },
  { value: 'collection', label: 'Подборка' },
  { value: 'status', label: 'Статус' },
];

/**
 * Поле ввода с чипами тегов: поиск по справочнику, несколько значений, AND в каталоге.
 */
export default function TagTokenInput({
  value,
  onChange,
  placeholder = 'Начните вводить название тега…',
  label = 'Теги (все выбранные должны быть у задачи)',
  showTypeFilter = false,
  compact = false,
}) {
  const [input, setInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const loadSuggestions = useCallback(
    async (q) => {
      setLoading(true);
      try {
        const params = { page_size: 100 };
        const trimmed = (q || '').trim();
        if (trimmed) params.search = trimmed;
        if (typeFilter) params.type = typeFilter;
        const res = await api.get('/tasks/tags/', { params });
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        const taken = new Set((value || []).map((t) => t.slug));
        setSuggestions(data.filter((t) => t?.slug && !taken.has(t.slug)));
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [value, typeFilter],
  );

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadSuggestions(input), 200);
    return () => clearTimeout(t);
  }, [input, open, loadSuggestions]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  function addTag(tag) {
    if (!tag?.slug || (value || []).some((t) => t.slug === tag.slug)) return;
    onChange([...(value || []), { id: tag.id, slug: tag.slug, name: tag.name, type: tag.type }]);
    setInput('');
    setSuggestions((prev) => prev.filter((t) => t.slug !== tag.slug));
    inputRef.current?.focus();
  }

  function removeTag(slug) {
    onChange((value || []).filter((t) => t.slug !== slug));
  }

  function onKeyDown(e) {
    if (e.key === 'Backspace' && !input && (value || []).length > 0) {
      e.preventDefault();
      const next = [...(value || [])];
      next.pop();
      onChange(next);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter' && open && suggestions.length > 0) {
      e.preventDefault();
      addTag(suggestions[0]);
    }
  }

  const typeSelect = showTypeFilter ? (
    <select
      className={compact ? 'form-select tag-token-type-select-compact' : 'form-input'}
      value={typeFilter}
      aria-label="Тип тега"
      onChange={(e) => {
        setTypeFilter(e.target.value);
        if (open) loadSuggestions(input);
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {TAG_TYPE_FILTER_OPTIONS.map((o) => (
        <option key={o.value || 'all'} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  ) : null;

  return (
    <div className={`tag-token-input-wrap${compact ? ' tag-token-input-wrap--compact' : ''}`} ref={wrapRef}>
      {!compact && <label className="tag-token-input-label">{label}</label>}
      {showTypeFilter && !compact && (
        <label className="form-label konspekt-tag-type-filter">
          Тип тега
          {typeSelect}
        </label>
      )}
      <div
        className={`tag-token-input${compact ? ' tag-token-input--compact' : ''}`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {compact && typeSelect}
        {(value || []).map((t) => (
          <span key={t.slug} className="tag-token-chip">
            <span className="tag-token-chip-text">{t.name}</span>
            <button
              type="button"
              className="tag-token-chip-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(t.slug);
              }}
              aria-label={`Удалить тег ${t.name}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="tag-token-input-field"
          placeholder={(value || []).length ? '' : placeholder}
          value={input}
          aria-label={compact ? label : undefined}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            loadSuggestions(input);
          }}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-expanded={open}
        />
      </div>
      {open && (
        <div className="tag-token-dropdown" role="listbox">
          {loading ? (
            <div className="tag-token-dropdown-muted">Загрузка…</div>
          ) : suggestions.length === 0 ? (
            <div className="tag-token-dropdown-muted">
              {input.trim()
                ? 'Нет совпадений'
                : showTypeFilter && typeFilter
                  ? 'Нет тегов этого типа (попробуйте ввести часть названия)'
                  : showTypeFilter
                    ? 'Введите часть названия или выберите тип тега'
                    : 'Введите текст для поиска'}
            </div>
          ) : (
            suggestions.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                className="tag-token-dropdown-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(t)}
              >
                <span>{t.name}</span>
                <span className="tag-token-dropdown-meta">{t.type}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
